import { EventSettingsData, LoginFormData, ResetPasswordFormData } from '@common/interfaces'
import AxiosInterceptorInstanceFd from '@common/axios/fd-backend/axios.interceptor'
import {
  EventContent,
  EventDetail,
  EventUrlFormData,
  ExhibitorDetail,
  Form,
  Question,
  Language,
  ILeads,
  IAnalyticsData,
  ISelectedLead,
  EmailTemplate,
  EmailTemplateFields
} from '@common/fd-api-interfaces'
import axios from 'axios'
import queryString from 'query-string'

/**
 * Fetches a list of events.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function fetchEvents(): Promise<EventDetail[]> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get('event', {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data.events.map((event: any) => event as EventDetail)
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches a list of leads.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function fetchLeads(params: any): Promise<ILeads[]> {
  const data = queryString.stringify(params)
  try {
    const response: any = await AxiosInterceptorInstanceFd.get(`lead?${data}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}
/**
 * Fetches a list of leads.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function fetchSelectedLead(id: number): Promise<ISelectedLead[]> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get(`lead/${id}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}
/**
 * Fetches a list of leads.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function getFileUrl(eventContentId: string): Promise<any> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get(`content/${eventContentId}/download`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data.downloadUrl
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of  an analytics data.
 *
 * @param - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function fetchAnalytics(data: any): Promise<IAnalyticsData | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(
      `analytics`,
      { ...data },
      {
        data: {},
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) return responseData.data as IAnalyticsData
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}
/**
 * Fetches a list of exhibitors.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function fetchExhibitors(eventId: string): Promise<ExhibitorDetail[]> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get(`exhibitor?eventId=${eventId}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data.exhibitors.map(
        (exhibitor: any) =>
          ({
            name: exhibitor.name,
            exhibitorId: exhibitor.exhibitorId,
            email: exhibitor.email,
            username: exhibitor.username,
            organisation: exhibitor.organisation,
            createdAt: exhibitor.createdAt
          }) as ExhibitorDetail
      )
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches a list of exhibitors.
 *
 * @returns A Promise that resolves to an array of events.
 */
export async function fetchExhibitorSingle(exhibitorId: string): Promise<ExhibitorDetail | null> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get(`exhibitor/${exhibitorId}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data
    }
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function saveEventDetail(event: any): Promise<EventDetail | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(
      `event`,
      { ...event },
      {
        data: {},
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) return responseData.data as EventDetail
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function saveExhibitor(exhibitor: any): Promise<ExhibitorDetail | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(
      `exhibitor`,
      { ...exhibitor },
      {
        data: {},
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) return responseData.data as ExhibitorDetail
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function updateExhibitor(exhibitorId: string, exhibitor: any): Promise<ExhibitorDetail | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.put(
      `exhibitor/${exhibitorId}`,
      { ...exhibitor },
      {
        data: {},
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) return responseData.data as ExhibitorDetail
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function fetchEventDetail(eventId: string): Promise<EventDetail | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get(`event/${eventId}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) return responseData.data as EventDetail
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function fetchEventContents(): Promise<EventContent[] | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get(`content`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) return responseData.data.contents as EventContent[]
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches the details of a specific event.
 *
 * @param eventId - The ID of the event to fetch.
 * @returns A Promise that resolves to the details of the event.
 */
export async function saveEventContentUrl(eventUrlFormData: EventUrlFormData): Promise<EventContent | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(`content/url`, eventUrlFormData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) return responseData.data as EventContent
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetch all default email templates
 *
 * @returns A Promise that resolves when the templates are fetched
 */
export async function fetchTemplates(): Promise<EmailTemplate[]> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get('email/templates', {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data.emailTemplates
    }
    return []
  } catch (err) {
    console.error(err, 'uploadEventFile')
    return Promise.reject(err)
  }
}

/**
 * Fetch all custom templates of logged in user.
 *
 * @returns A Promise that resolves when the templates are fetched
 */
export async function fetchCustomEmailTemplates(): Promise<EmailTemplate[]> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get('email/templates/custom', {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data.emailTemplates
    }
    return []
  } catch (err) {
    console.error(err, 'uploadEventFile')
    return Promise.reject(err)
  }
}

/**
 * Fetch default email templates.
 *
 * @returns A Promise that resolves to the list of default email template.
 */
export async function fetchDefaultEmailTemplate(): Promise<EmailTemplate | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get(`email/templates/default`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    console.error(err, 'fetchDefaultEmailTemplate')
    return Promise.reject(err)
  }
}

/**
 * Updates the default email template.
 *
 * @param emailTemplateId - The ID of the email template.
 * @returns A Promise that resolves to the updated data.
 */
export async function updateDefaultEmailTemplate(emailTemplateId: number): Promise<EmailTemplate | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.put(
      `email/templates/default`,
      { emailTemplateId },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    console.error(err, 'updateDefaultEmailTemplate')
    return Promise.reject(err)
  }
}

/**
 * save custom email template.
 *
 * @param data - contains custom email template properties.
 * @returns A Promise that resolves to the custom email template data.
 */
export async function saveCustomEmailTemplate(data: EmailTemplateFields): Promise<EmailTemplate | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(
      `email/templates/custom`,
      { ...data },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    console.error(err, 'saveCustomEmailTemplate')
    return Promise.reject(err)
  }
}

/**
 * update custom email template.
 *
 * @param data - contains custom email template properties.
 * @returns A Promise that resolves to the custom email template data.
 */
export async function updateCustomEmailTemplate(id: number, data: EmailTemplateFields): Promise<EmailTemplate | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.put(
      `email/templates/custom/${id}`,
      { ...data },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    console.error(err, 'updateCustomEmailTemplate')
    return Promise.reject(err)
  }
}

/**
 * delete custom email template data by id.
 *
 * @param id - custom email template id.
 * @returns A Promise that resolves success.
 */
export async function deleteCustomEmailTemplate(id: number): Promise<{} | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.delete(`email/templates/custom/${id}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    console.error(err, 'deleteCustomEmailTemplate')
    return Promise.reject(err)
  }
}

/**
 * Deletes a file associated with a specific event.
 *
 * @param eventId - The ID of the event.
 * @param fileBackendId - The ID of the file to delete.
 * @returns A Promise that resolves when the file is deleted.
 */
export async function deleteEventFile(eventContentId: number): Promise<any | null> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.delete(`content/${eventContentId}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return responseData
  } catch (err) {
    console.error(err, 'deleteEventFile')
    return Promise.reject(err)
  }
}

/**
 * Uploads a file for a specific event.
 *
 * @param eventId - The ID of the event.
 * @param file - The file to upload.
 * @returns A Promise that resolves when the file is uploaded.
 */
export async function uploadEventContentFile(file: File): Promise<any | null> {
  try {
    const presignedFormData = new FormData()
    presignedFormData.append('displayName', file.name)
    presignedFormData.append('type', file.type)

    const responseData: any = await AxiosInterceptorInstanceFd.post(`content/presigned`, presignedFormData, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      const uploadPreSignedUrl = responseData.data.preSignedUrl
      await axios.put(uploadPreSignedUrl, file)
    }

    return responseData
  } catch (err) {
    console.error(err, 'uploadEventContentFile')
    return Promise.reject(err)
  }
}

/**
 * Fetch all templates
 *
 * @returns A Promise that resolves when the templates are fetched
 */
export async function fetchForm(): Promise<Form> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get('form', {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data
    }
    return {} as Form
  } catch (err) {
    console.error(err, 'fetchForm')
    return Promise.reject(err)
  }
}

/**
 * Fetch all templates
 *
 * @returns A Promise that resolves when the templates are fetched
 */
export async function updateForm(formId: number, questionsArray: Question[]): Promise<Form> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.put(
      `form/${formId}`,
      { questions: questionsArray },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (responseData.success) {
      return responseData.data
    }
    return {} as Form
  } catch (err) {
    console.error(err, 'fetchForm')
    return Promise.reject(err)
  }
}

/**
 * Send report for a specific event.
 *
 * @param eventId - The ID of the event.
 * @returns A Promise that resolves when the request is done.
 */
export async function sendReport(eventId: string): Promise<void> {
  try {
    await AxiosInterceptorInstanceFd.get(`reports/${eventId}`, {
      data: {},
      headers: {
        'Content-Type': 'application/json'
      }
    })
    return
  } catch (err) {
    console.error(err, 'sendReport')
    return Promise.reject(err)
  }
}

/**
 * login
 * @param loginFormData - The form data object.
 * @returns A Promise that resolves when the request is done.
 */
export async function submitLogin(loginFormData: LoginFormData): Promise<void> {
  const { username, password } = loginFormData
  return AxiosInterceptorInstanceFd.post(
    'auth/login',
    { username, password },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}
/**
 * Fetch current authenticated user profile from HttpOnly auth_token cookie
 */
export async function fetchMe(): Promise<any> {
  return AxiosInterceptorInstanceFd.get('auth/me', {
    headers: { 'Content-Type': 'application/json' }
  })
}

/**
 * logout
 * @returns A Promise that resolves when the request is done.
 */
export async function doLogout(): Promise<void> {
  return AxiosInterceptorInstanceFd.get('auth/logout', {
    headers: {
      'Content-Type': 'application/json'
    }
  })
}

/**
 * Forgot Password
 * @param username - The email address to share reset password.
 * @returns A Promise that resolves when the request is done.
 */
export async function forgotPassword(username: string): Promise<void> {
  return AxiosInterceptorInstanceFd.post(
    'auth/forgot-password',
    { username },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}

/**
 * Reset Password
 * @param resetFormData - The form data object.
 * @returns A Promise that resolves when the request is done.
 */
export async function resetPassword(resetFormData: ResetPasswordFormData): Promise<void> {
  const { token, password, exhibitorId } = resetFormData
  return AxiosInterceptorInstanceFd.post(
    'auth/reset-password',
    { token, password, exhibitorId },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}

/**
 * Validate Reset Password Token
 * @param token - The token to be validate.
 * @returns A Promise that resolves when the request is done.
 */
export async function validateAccessToken(token: string, exhibitorId: number): Promise<void> {
  return AxiosInterceptorInstanceFd.post(
    'auth/reset-token/validate',
    { token, exhibitorId },
    {
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}

/**
 * create report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function generateReport(reportType: string, eventId: string): Promise<any> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.post(
      `report/${reportType}/${eventId}/generate`,
      {},
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      }
    )
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * get report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function getReport(reportId: number): Promise<any> {
  try {
    const responseData: any = await AxiosInterceptorInstanceFd.get(`report/${reportId}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (responseData.success) {
      return responseData.data
    }
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * get report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function getLastReportDetails(reportType: string, eventId: string): Promise<any> {
  try {
    const latestReportResponse: any = await AxiosInterceptorInstanceFd.get(`report/${reportType}/${eventId}/latest`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (latestReportResponse.success) return latestReportResponse.data
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * get report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function downloadReport(reportId: number): Promise<any> {
  try {
    const downloadReportResponse: any = await AxiosInterceptorInstanceFd.post(
      `report/${reportId}/download`,
      { downloadAs: 'link' },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    if (downloadReportResponse.success) {
      return downloadReportResponse.data
    }
    return null
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * get report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function downloadContent(fileId: number, fileName: string): Promise<any> {
  try {
    const downloadReportResponse: any = await AxiosInterceptorInstanceFd.get(`content/download/${fileId}/${fileName}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (downloadReportResponse.success) {
      return downloadReportResponse.data
    } else {
      return {
        errorMessage: downloadReportResponse.exception
      }
    }
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * get report
 * @param eventId - Campaign Id.
 * @returns A Promise that resolves when the request is done.
 */
export async function downloadLeadFile(leadId: number, fileId: number, fileName: string): Promise<any> {
  try {
    const downloadReportResponse: any = await AxiosInterceptorInstanceFd.get(`lead/${leadId}/download/${fileId}/${fileName}`, {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (downloadReportResponse.success) {
      return downloadReportResponse.data
    } else {
      return {
        errorMessage: downloadReportResponse.exception
      }
    }
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * update event settings
 * @param eventSettings - The form data object.
 * @returns A Promise that resolves when the request is done.
 */
export async function updateEventSettings(eventId: string, eventSettingsData: EventSettingsData): Promise<boolean> {
  try {
    const { dailyReportsEnabled, archivalDays } = eventSettingsData
    const updateEventSettingsResponse: any = await AxiosInterceptorInstanceFd.put(
      `event/${eventId}/settings`,
      { dailyReportsEnabled, archivalDays },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    return updateEventSettingsResponse.success
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Fetches available languages.
 *
 * @returns A Promise that resolves to an array of languages.
 */
export async function fetchLanguageList(): Promise<Language[]> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.get('language/list', {
      headers: {
        'Content-Type': 'application/json'
      }
    })
    if (response.success) {
      return response.data.languages.map((language: any) => language as Language)
    }
    return []
  } catch (err) {
    return Promise.reject(err)
  }
}

/**
 * Update default languages.
 *
 * @returns A Promise that resolves the update status.
 */
export async function updateDefaultLanguage(languageId: number): Promise<any> {
  try {
    const response: any = await AxiosInterceptorInstanceFd.put(
      'language/default',
      { languageId },
      {
        headers: {
          'Content-Type': 'application/json'
        }
      }
    )
    return response
  } catch (err) {
    return Promise.reject(err)
  }
}
